import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { SCOPE_GROUPS_QUERY_SEPARATOR } from '@this+that/constants';
import {
  MessagingPlatform,
  MicrosoftIntegrationStatus,
  type OutlookScopeGroup,
  TeamWithChannels,
  UnifiedAttachment,
} from '@this+that/models';
import log from 'loglevel';

import { createApiWithAuth } from '../utils/axios';

let microsoftApi: ReturnType<typeof createApiWithAuth> | null = null;

const getMicrosoftApi = () => {
  if (!microsoftApi) {
    const baseUrl = import.meta.env.VITE_MICROSOFT_BASE_URL;
    if (!baseUrl) {
      throw new Error('Microsoft base URL is not set');
    }
    microsoftApi = createApiWithAuth(baseUrl);
  }
  return microsoftApi;
};

// Every caller names its groups: a card, or a mailbox entry point asking for mail.
export const getMicrosoftIntegrationUrl = async (
  isOnboarding: boolean,
  scopeGroups: readonly OutlookScopeGroup[],
) => {
  try {
    const { data } = await getMicrosoftApi().get('/oauth-url', {
      params: {
        isOnboarding,
        platform: MessagingPlatform.OUTLOOK,
        scopeGroups: scopeGroups.join(SCOPE_GROUPS_QUERY_SEPARATOR),
      },
    });
    return data;
  } catch (error) {
    log.error('Error fetching authentication data:', error);
    throw error;
  }
};

export const scheduleMicrosoftInitialSync = async (
  code: string,
  state: string | null,
  isOnboarding: boolean = false,
) => {
  try {
    const { data } = await getMicrosoftApi().post('/initial-sync', {
      code,
      state,
      isOnboarding,
      platform: MessagingPlatform.OUTLOOK,
    });
    return data;
  } catch (error) {
    log.error('Error scheduling initial synchronization:', error);
    throw error;
  }
};

// The ONE card being clicked: mail and calendar leave apart, and Microsoft access only ends with both.
export const disconnectOutlookIntegration = async (
  homeAccountId: string,
  deleteData: boolean,
  scopeGroup: OutlookScopeGroup,
) => {
  try {
    const { data } = await getMicrosoftApi().post('/unsubscribe', {
      homeAccountId,
      platform: MessagingPlatform.OUTLOOK,
      deleteData,
      scopeGroup,
    });
    return data;
  } catch (error) {
    log.error('Error unsubscribing outlook:', error);
    throw error;
  }
};

export const getTeamsIntegrationUrl = async () => {
  try {
    const { data } = await getMicrosoftApi().get(
      `/oauth-url?platform=${MessagingPlatform.TEAMS}`,
    );
    return data;
  } catch (error) {
    log.error('Error fetching Teams authentication URL:', error);
    throw error;
  }
};

export const scheduleTeamsInitialSync = async (
  code: string,
  state: string | null,
) => {
  try {
    const { data } = await getMicrosoftApi().post('/initial-sync', {
      code,
      state,
      platform: MessagingPlatform.TEAMS,
    });
    return data;
  } catch (error) {
    log.error('Error scheduling Teams initial synchronization:', error);
    throw error;
  }
};

export const disconnectTeamsIntegration = async (
  homeAccountId: string,
  deleteData: boolean,
) => {
  try {
    const { data } = await getMicrosoftApi().post('/unsubscribe', {
      homeAccountId,
      platform: MessagingPlatform.TEAMS,
      deleteData,
    });
    return data;
  } catch (error) {
    log.error('Error unsubscribing Teams integration:', error);
    throw error;
  }
};

export const getTeamsChats = async (homeAccountId: string) => {
  try {
    const { data } = await getMicrosoftApi().get('/get-chat-messages', {
      params: { homeAccountId },
    });
    return data;
  } catch (error) {
    log.error('Error fetching Teams chats:', error);
    throw error;
  }
};

export const getUserTeamsWithChannels = async (
  homeAccountId: string,
): Promise<TeamWithChannels[]> => {
  try {
    const { data } = await getMicrosoftApi().get('/get-user-teams', {
      params: { homeAccountId },
    });
    return data;
  } catch (error) {
    log.error('Error fetching Teams with channels:', error);
    throw error;
  }
};

export interface SendTeamsMessageBody {
  homeAccountId: string;
  chatId: string;
  text?: string;
  attachments?: UnifiedAttachment[];
}

export const sendTeamsMessage = async ({
  homeAccountId,
  chatId,
  text,
  attachments,
}: SendTeamsMessageBody) => {
  try {
    const { data } = await getMicrosoftApi().post('/send-message', {
      homeAccountId,
      chatId,
      text,
      attachments,
    });
    return data;
  } catch (error) {
    log.error('Error sending Teams message:', error);
    throw error;
  }
};

export const markChatAsRead = async (homeAccountId: string, chatId: string) => {
  try {
    const { data } = await getMicrosoftApi().post('/mark-chat-as-read', {
      homeAccountId,
      chatId,
    });
    return data;
  } catch (error) {
    log.error('Error marking Teams chat as read:', error);
    throw error;
  }
};

const GET_USER_MICROSOFT_INTEGRATION_STATUS = gql`
  query getUserMicrosoftIntegrationStatus {
    getUserMicrosoftIntegrationStatus {
      userId
      homeAccountId
      outlookAddress
      outlookEnabled
      calendarEnabled
      teamsEnabled
    }
  }
`;

interface GetUserMicrosoftIntegrationStatusData {
  getUserMicrosoftIntegrationStatus: MicrosoftIntegrationStatus[];
}

export function useGetUserMicrosoftIntegrationStatus() {
  const { loading, refetch } = useQuery<GetUserMicrosoftIntegrationStatusData>(
    GET_USER_MICROSOFT_INTEGRATION_STATUS,
    { skip: true },
  );

  const getUserMicrosoftIntegrationStatus = useCallback(async (): Promise<
    MicrosoftIntegrationStatus[] | null
  > => {
    try {
      const result = await refetch();

      if (result.error || !result.data) {
        log.error('Error fetching Microsoft integration status:', result.error);
        return null;
      }

      return result.data.getUserMicrosoftIntegrationStatus;
    } catch (error) {
      log.error('Error fetching Microsoft integration status:', error);
      return null;
    }
  }, [refetch]);

  return { getUserMicrosoftIntegrationStatus, loading };
}
